import React from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import Autocomplete from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Divider from '@mui/material/Divider';

import { getUniqueScoreValues, scoreToneFromValue, scoreToggleButtonSx } from './scoreUtils';
import { bandChipSx, bandTone, formatBandRange } from './assessmentUiUtils';

const AssessmentScoringControls = ({
  bands,
  sortedBands,
  bandCounts,
  readOnly,
  sortedCriteria,
  bulkCriterion,
  setBulkCriterion,
  bulkScore,
  setBulkScore,
  applyScoreToAll,
  saving,
  focusCriterion,
  setFocusCriterion,
}) => {
  return (
    <>
      {(bands || []).length > 0 ? (
        <Paper variant="outlined" sx={{ p: 1.25, borderRadius: 2, mb: 1.5 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Sonuç kategorileri (puan aralıkları)
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            {(sortedBands || []).map((b) => {
              const label = String(b?.label || '—');
              const range = formatBandRange(b);
              const countText = readOnly ? `: ${Number(bandCounts?.[String(b.id)] || 0)}` : '';
              const tone = bandTone(label);
              return (
                <Chip
                  key={String(b.id)}
                  size="small"
                  variant="outlined"
                  color={tone}
                  sx={bandChipSx(tone)}
                  label={`${label} (${range})${countText}`}
                />
              );
            })}
          </Box>
        </Paper>
      ) : null}

      <Paper variant="outlined" sx={{ p: 1.25, borderRadius: 2, mb: 1.5 }}>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          Toplu uygula (tek ölçüt → tüm öğrencilere)
        </Typography>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
          <Autocomplete
            size="small"
            options={sortedCriteria}
            value={bulkCriterion}
            getOptionLabel={(opt) => {
              const section = opt?.section_title ? `${String(opt.section_title)} — ` : '';
              return `${section}${String(opt?.name || '')}`;
            }}
            onChange={(_, v) => {
              setBulkCriterion(v || null);
              setBulkScore(null);
            }}
            disabled={readOnly || saving}
            sx={{ minWidth: 280, flex: 1 }}
            renderInput={(params) => <TextField {...params} label="Ölçüt" placeholder="Seçin" />}
          />

          {(() => {
            const raw = Array.isArray(bulkCriterion?.score_values) ? bulkCriterion.score_values : [0, 1, 2, 3];
            const uniq = getUniqueScoreValues(raw, [0, 1, 2, 3]);

            if (uniq.length === 0) {
              return (
                <Autocomplete
                  size="small"
                  options={raw}
                  value={bulkScore}
                  onChange={(_, v) => setBulkScore(v)}
                  disabled={readOnly || saving || !bulkCriterion}
                  sx={{ width: 160 }}
                  renderInput={(params) => <TextField {...params} label="Puan" />}
                />
              );
            }

            return (
              <ToggleButtonGroup
                size="small"
                exclusive
                value={bulkScore === null || bulkScore === undefined ? null : Number(bulkScore)}
                onChange={(_, v) => setBulkScore(v)}
                disabled={readOnly || saving || !bulkCriterion}
                sx={{ flexWrap: 'wrap', '& .MuiToggleButton-root': { px: 1.25 } }}
              >
                {uniq.map((v) => (
                  <ToggleButton key={String(v)} value={Number(v)} sx={scoreToggleButtonSx(scoreToneFromValue(v, uniq))}>
                    {String(v)}
                  </ToggleButton>
                ))}
              </ToggleButtonGroup>
            );
          })()}

          <Button
            variant="outlined"
            onClick={applyScoreToAll}
            disabled={readOnly || saving || !bulkCriterion || bulkScore === null || bulkScore === undefined}
          >
            Uygula
          </Button>
        </Box>
      </Paper>

      <Paper variant="outlined" sx={{ p: 1.25, borderRadius: 2, mb: 1.5 }}>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          Ölçüt filtresi (opsiyonel) — sadece seçili ölçütü göster
        </Typography>
        <Autocomplete
          size="small"
          options={sortedCriteria}
          value={focusCriterion}
          getOptionLabel={(opt) => {
            const section = opt?.section_title ? `${String(opt.section_title)} — ` : '';
            return `${section}${String(opt?.name || '')}`;
          }}
          onChange={(_, v) => setFocusCriterion(v || null)}
          disabled={saving}
          sx={{ minWidth: 280, maxWidth: 720 }}
          renderInput={(params) => <TextField {...params} label="Ölçüt" placeholder="(Boş bırak → tümü)" />}
          clearOnEscape
        />
      </Paper>

      <Divider sx={{ my: 1 }} />
    </>
  );
};

export default AssessmentScoringControls;
